import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  emailGetConfig, emailSaveConfig, emailSendTest,
} from "../../../redux/actions/email-system-actions";

const PROVIDERS = [
  { value: "SES", label: "AWS SES" },
  { value: "SMTP", label: "SMTP" },
];

const STATUS_STYLE = {
  CONNECTED: { bg: "#ECFDF5", color: "#047857", label: "Connected" },
  UNCONFIGURED: { bg: "#FEF3C7", color: "#92400E", label: "Not configured" },
  FAILED: { bg: "#FEE2E2", color: "#991B1B", label: "Connection failed" },
};

const EmailSettings = () => {
  const dispatch = useDispatch();
  const config = useSelector((s) => s.emailSystemSliceReducer.config);
  const loading = useSelector((s) => !!s.emailSystemSliceReducer.loading.config);
  const testResult = useSelector((s) => s.emailSystemSliceReducer.testResult);

  // Derive orgId from logged-in user record — fallback to 6 (our tenant)
  const orgId = useSelector((s) => s.signinSliceReducer?.tenantId || s.signinSliceReducer?.organizationId) || 6;

  const [form, setForm] = useState({});
  const [testEmail, setTestEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  useEffect(() => { document.title = "Email settings - Ample Hire"; dispatch(emailGetConfig({ orgId })); }, [dispatch, orgId]);
  useEffect(() => { if (config) setForm(config); }, [config]);

  const onChange = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: val }));
  };

  const save = async () => {
    setSaving(true); setSaveMsg(null);
    try {
      const payload = { ...form, orgId };
      if (payload.smtpPasswordMasked === "********") delete payload.smtpPassword;
      delete payload.smtpPasswordMasked;
      await dispatch(emailSaveConfig(payload));
      setSaveMsg("Settings saved");
      setTimeout(() => setSaveMsg(null), 2500);
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) return;
    setTesting(true);
    try {
      await dispatch(emailSendTest({ orgId, to: testEmail.trim() }));
    } finally { setTesting(false); }
  };

  const status = STATUS_STYLE[form.connectionStatus || "UNCONFIGURED"] || STATUS_STYLE.UNCONFIGURED;

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "24px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20 }}>
        <div>
          <h1 data-guide="email-config" style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Email settings</h1>
          <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0" }}>
            Configure how Ample Hire sends invitations, reminders, and notifications to candidates.
          </p>
        </div>
      </div>

      {loading && <div style={{ color: "#64748B", padding: 16 }}>Loading…</div>}

      {!loading && (
        <>
          {/* Connection status card */}
          <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 10, height: 10, borderRadius: 5, background: status.color }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>{status.label}</div>
                {form.lastTestedAt && (
                  <div style={{ fontSize: 11, color: "#94A3B8" }}>Last tested {new Date(form.lastTestedAt).toLocaleString()}</div>
                )}
              </div>
            </div>
            <span style={{ fontSize: 11, padding: "4px 10px", borderRadius: 100, fontWeight: 600, background: status.bg, color: status.color }}>
              {status.label.toUpperCase()}
            </span>
          </div>

          {/* Provider */}
          <Section title="Provider">
            <Field label="Email provider">
              <select value={form.provider || "SES"} onChange={onChange("provider")} style={inputStyle}>
                {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Field>

            {form.provider === "SMTP" ? (
              <>
                <Row>
                  <Field label="SMTP host"><input value={form.smtpHost || ""} onChange={onChange("smtpHost")} placeholder="smtp.example.com" style={inputStyle} /></Field>
                  <Field label="Port"><input type="number" value={form.smtpPort || 587} onChange={onChange("smtpPort")} style={inputStyle} /></Field>
                </Row>
                <Row>
                  <Field label="Username"><input value={form.smtpUsername || ""} onChange={onChange("smtpUsername")} style={inputStyle} /></Field>
                  <Field label="Password"><input type="password" value={form.smtpPasswordMasked || form.smtpPassword || ""} onChange={onChange("smtpPassword")} placeholder="••••••••" style={inputStyle} /></Field>
                </Row>
              </>
            ) : (
              <Row>
                <Field label="AWS region"><input value={form.sesRegion || "ap-south-1"} onChange={onChange("sesRegion")} style={inputStyle} /></Field>
                <Field label="Inbound domain (optional)"><input value={form.inboundDomain || ""} onChange={onChange("inboundDomain")} placeholder="reply.example.com" style={inputStyle} /></Field>
              </Row>
            )}
          </Section>

          {/* Sender identity */}
          <Section title="Sender identity">
            <Row>
              <Field label="From email"><input value={form.sesFromEmail || ""} onChange={onChange("sesFromEmail")} placeholder="hiring@example.com" style={inputStyle} /></Field>
              <Field label="From name"><input value={form.sesFromName || ""} onChange={onChange("sesFromName")} placeholder="Your Hiring Team" style={inputStyle} /></Field>
            </Row>
            <Field label="Reply-to address"><input value={form.sesReplyTo || ""} onChange={onChange("sesReplyTo")} placeholder="no-reply@example.com" style={inputStyle} /></Field>
          </Section>

          {/* Limits + behaviour */}
          <Section title="Limits and behaviour">
            <Row>
              <Field label="Daily send limit"><input type="number" value={form.dailySendLimit || 1000} onChange={onChange("dailySendLimit")} style={inputStyle} /></Field>
              <Field label="Sends today"><input value={form.sendsToday ?? 0} disabled style={{ ...inputStyle, background: "#F8FAFC", color: "#64748B" }} /></Field>
            </Row>
            <Toggle label="Enable open tracking" checked={!!form.trackingEnabled} onChange={onChange("trackingEnabled")} />
            <Toggle label="Enable auto-reply" checked={!!form.autoReplyEnabled} onChange={onChange("autoReplyEnabled")} />
            <Toggle label="Enable AI processing of replies" checked={!!form.aiProcessingEnabled} onChange={onChange("aiProcessingEnabled")} />
          </Section>

          {/* Signature */}
          <Section title="Email signature">
            <Field label="HTML signature appended to every outbound email">
              <textarea
                rows={5}
                value={form.signatureHtml || ""}
                onChange={onChange("signatureHtml")}
                placeholder="<p>Best,<br>The Hiring Team</p>"
                style={{ ...inputStyle, fontFamily: "monospace", fontSize: 12, resize: "vertical" }}
              />
            </Field>
          </Section>

          {/* Test + save */}
          <Section title="Test connection">
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <Field label="Send a test email to" style={{ flex: 1 }}>
                <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" style={inputStyle} />
              </Field>
              <button
                onClick={sendTest}
                disabled={testing || !testEmail.trim()}
                style={{ padding: "10px 18px", borderRadius: 8, background: testing || !testEmail.trim() ? "#CBD5E1" : "#7B5AC4", color: "white", border: "none", fontWeight: 600, fontSize: 13, cursor: testing || !testEmail.trim() ? "not-allowed" : "pointer" }}
              >
                {testing ? "Sending…" : "Send test"}
              </button>
            </div>
            {testResult && (
              <div style={{ marginTop: 10, padding: 10, background: "#ECFDF5", border: "1px solid #10B981", borderRadius: 6, fontSize: 12, color: "#047857" }}>
                ✓ {testResult.message}
                <div style={{ fontSize: 11, color: "#047857", opacity: 0.7, marginTop: 2 }}>
                  Stub mode: no actual SMTP/SES call was made. Logged as row #{testResult.logId}.
                </div>
              </div>
            )}
          </Section>

          <div style={{ position: "sticky", bottom: 0, background: "white", padding: "12px 0", borderTop: "1px solid #E2E8F0", display: "flex", justifyContent: "flex-end", gap: 12, alignItems: "center" }}>
            {saveMsg && <span style={{ fontSize: 12, color: "#047857" }}>✓ {saveMsg}</span>}
            <button
              onClick={save}
              disabled={saving}
              style={{ padding: "10px 24px", borderRadius: 8, background: saving ? "#CBD5E1" : "#7B5AC4", color: "white", border: "none", fontWeight: 600, fontSize: 14, cursor: saving ? "not-allowed" : "pointer" }}
            >
              {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const Section = ({ title, children }) => (
  <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16, marginBottom: 14 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>{title}</div>
    {children}
  </div>
);

const Row = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 10 }}>{children}</div>
);

const Field = ({ label, children, style }) => (
  <div style={{ marginBottom: 10, ...style }}>
    <div style={{ fontSize: 11, color: "#64748B", marginBottom: 4, fontWeight: 500 }}>{label}</div>
    {children}
  </div>
);

const Toggle = ({ label, checked, onChange }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", fontSize: 13, color: "#334155", cursor: "pointer" }}>
    <input type="checkbox" checked={!!checked} onChange={onChange} style={{ accentColor: "#7B5AC4" }} />
    {label}
  </label>
);

const inputStyle = {
  width: "100%", padding: "8px 12px", border: "1px solid #E2E8F0", borderRadius: 6,
  fontSize: 13, color: "#0F172A", outline: "none", background: "white", boxSizing: "border-box",
};

export default EmailSettings;
